import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import XIcon from '../../icons/X.svg';
import InformationDarkGreen from '../../icons/Info-icon-dark-green.svg';
import DatePickerWrapper from '../DatePickerWrapper/DatePickerWrapper';
import * as translator from '../../utils/translator';
import InputField from "../CredentialsInput/InputField";
import { validateCustomerName } from "../../utils/fieldsValidator";

interface Props {
    language: string;
    onSubmit: (data: { dateOfBirth: string; firstName: string; lastName: string; gender: "male" | "female" }) => void;
    defaultValues?: Partial<{ dateOfBirth: string; firstName: string; lastName: string; gender: "male" | "female" }>;
    onClose?: () => void;
}

const OAuthAdditionalInfoModal: React.FC<Props> = ({ onSubmit, onClose, language, defaultValues }) => {

    const additionalInfoSchema = z.object({
        dateOfBirth: z
            .string()
            .min(1, 'Date of birth is required'),
        firstName: z
            .string()
            .refine(
                (value: string) => {
                    const { isValid } = validateCustomerName(value, language);
                    return isValid;
                },
                (value) => ({
                    message: validateCustomerName(value, language).errorMsg
                })
            ),
        lastName: z
            .string()
            .refine(
                (value: string) => {
                    const { isValid } = validateCustomerName(value, language);
                    return isValid;
                },
                (value) => ({
                    message: validateCustomerName(value, language).errorMsg
                })
            ),
        gender: z.enum(['male', 'female'], { errorMap: () => ({ message: 'Please select a gender' }) })
    });

    type AdditionalInfoData = z.infer<typeof additionalInfoSchema>;

    const {  handleSubmit, watch, setValue, formState: { errors, isSubmitting, isValid }, trigger } = useForm<AdditionalInfoData>({
        resolver: zodResolver(additionalInfoSchema),
        mode: 'all', 
        defaultValues: {
            dateOfBirth: '',
            firstName: defaultValues?.firstName || '',
            lastName: defaultValues?.lastName || '',
            gender: undefined
        }
    });

    // Trigger validation on mount for prefilled values
    useEffect(() => {
        if (defaultValues?.firstName) {
            trigger('firstName'); 
        }
        if (defaultValues?.lastName) {
            trigger('lastName'); 
        }
    }, [trigger, defaultValues]);

    const watchedGender = watch('gender');

    return (
        <div id='oauth-additional-info-modal' className="bg-white rounded-lg shadow-xl w-full overflow-y-auto z-[999] max-w-[400px]">

            {/* Header */}
            <div className="flex justify-between items-center py-4 px-6" style={{ borderBottom: '1px solid #9A9A9A40', borderRadius: 10 }}>
                <div className="flex items-center gap-2">
                    <img src={InformationDarkGreen} alt="i" className="w-6 h-6" />
                    <h2 className="text-lg font-medium text-[#0A9281] ">{translator.translate(language, "default", "oauth/additionalinfomodal/title")}</h2>
                </div>

                <img src={XIcon} alt="X" className="w-6 h-6 cursor-pointer" onClick={onClose} />

            </div>

            {/* Body */}
            <div className="p-6">
                <p className="text-base text-gray-700 mb-4 leading-relaxed">
                    {translator.translate(language, "default", "oauth/additionalinfomodal/subtitle")}
                </p>

                <form id='o-auth-additional-info-form' onSubmit={handleSubmit(onSubmit)} className="space-y-5">

                    {/* First Name Field */}
                    <div className="space-y-2 w-full ">
                        <InputField
                            id="additional-info-modal-first-name"
                            ignoreTouched={true}
                            value={watch('firstName')}
                            onChange={(value: string) => {
                            setValue('firstName', value, { shouldValidate: true });
                            }}
                            placeholder={translator.translate(language, "shipping", "ShippingForm/first_name")}
                            mandatory={true}
                            errorMsg={errors.firstName?.message}
                            language={language}
                            onBlur={() => {}}
                        />
                    </div>

                    {/* Last Name Field */}
                    <div className="space-y-2 w-full ">
                        <InputField
                            id="additional-info-modal-last-name"
                            ignoreTouched={true}
                            value={watch('lastName')}
                            onChange={(value: string) => {
                            setValue('lastName', value, { shouldValidate: true });
                            }}
                            placeholder={translator.translate(language, "shipping", "ShippingForm/surname")}
                            mandatory={true}
                            errorMsg={errors.lastName?.message}
                            language={language}
                            onBlur={() => {}}
                        />
                    </div>



                    {/* Date of Birth Field */}
                    <div className="space-y-2 w-full ">
                        <label htmlFor="dateOfBirth" className="font-roboto block text-sm font-medium text-[#98A0AF]">
                            {translator.translate(language, "default", "oauth/additionalinfomodal/date_of_birth_label")} <span className="text-red-500">*</span>
                        </label>

                        <DatePickerWrapper
                            onChange={(date: string) => setValue('dateOfBirth', date, { shouldValidate: true })}
                            errorMode={!!errors.dateOfBirth}
                            value={watch('dateOfBirth')}
                            isBirthdayInput={true}
                            isBodyLevelPortal={false}
                        />
                        
                    </div>

                    {/* Gender Field */}
                    <div className="">
                        <label className="font-roboto block text-sm font-medium text-[#98A0AF] mb-2">
                            {translator.translate(language, "default", "oauth/additionalinfomodal/gender_label")} <span className="text-red-500">*</span>
                        </label>
                        <div style={{ display: 'flex', gap: '0px' }}>
                            <div
                                id='male-button'
                                style={{
                                    flex: 1,
                                    padding: '16px 20px',
                                    border: watchedGender === 'male' ? '1px solid #0A9281' : '1px solid #E5E7EB',
                                    borderRadius: '12px 0px 0px 12px',
                                    borderRight: watchedGender === 'male' ? '1px solid #0A9281' : '1px solid #E5E7EB',
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    fontSize: '16px',
                                    fontWeight: watchedGender === 'male' ? '500' : '400',
                                    color: watchedGender === 'male' ? '#0A9281' : '#9CA3AF',
                                    backgroundColor: 'transparent',
                                    transition: 'all 0.2s ease',
                                    minHeight: '48px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxSizing: 'border-box'
                                }}
                                onClick={() => setValue('gender', 'male', { shouldValidate: true })}
                            >
                                {translator.translate(language, "default", "oauth/additionalinfomodal/gender_male")}
                            </div>
                            <div
                                id='female-button'
                                style={{
                                    flex: 1,
                                    padding: '16px 20px',
                                    border: watchedGender === 'female' ? '1px solid #0A9281' : '1px solid #E5E7EB',
                                    borderRadius: '0px 12px 12px 0px',
                                    borderLeft: watchedGender === 'female' ? '1px solid #0A9281' : '1px solid #E5E7EB',
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    fontSize: '16px',
                                    fontWeight: watchedGender === 'female' ? '500' : '400',
                                    color: watchedGender === 'female' ? '#0A9281' : '#9CA3AF',
                                    backgroundColor: 'transparent',
                                    transition: 'all 0.2s ease',
                                    minHeight: '48px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxSizing: 'border-box'
                                }}
                                onClick={() => setValue('gender', 'female', { shouldValidate: true })}
                            >
                                {translator.translate(language, "default", "oauth/additionalinfomodal/gender_female")}
                            </div>
                        </div>
                        {errors.gender && (
                            <span className="text-red-500 text-sm">{errors.gender.message}</span>
                        )}
                    </div>

                    {/* Submit Button */}
                    <button
                        type="submit"
                        style={{border: 'none'}}
                        className={`w-full py-4 px-6 rounded-md text-base font-semibold mt-2 
                            ${!isValid || isSubmitting ? 'bg-gray-400 text-white cursor-not-allowed' : 'bg-[#0A9281] text-white hover:bg-[#0A9281]/95 cursor-pointer' }`}
                           
                        disabled={!isValid || isSubmitting}
                    >
                        {isSubmitting ? translator.translate(language, "default", "oauth/additionalinfomodal/submit_button_loading") : translator.translate(language, "default", "oauth/additionalinfomodal/submit_button")}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default OAuthAdditionalInfoModal;